import { useQueryClient } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { SectionTitle, Stat, Stats } from 'shared/components/section'
import { ToneNote } from 'shared/components/tone-banner'
import { Button } from 'shared/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from 'shared/components/ui/dialog'
import { Input } from 'shared/components/ui/input'
import { Label } from 'shared/components/ui/label'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate } from 'shared/lib/i18n'
import { formatDayTime } from 'shared/lib/utils/date'
import { MONEY_COLUMN, som, sum, WHOLE } from 'shared/lib/money'

/** The drivers who carry Cash bags now, each with the bags they carry (`cashBags.Money`). */
export type DriverWithBags = RouterOutputs['cashBags']['Money']['drivers'][number]
type DriverBag = DriverWithBags['bags'][number]
export type CashBagReceiveRequest = RouterInputs['cashBags']['Receive']
export type CashBagReceiveAnswer = RouterOutputs['cashBags']['Receive']
type Received = Extract<CashBagReceiveAnswer, { outcome: 'received' | 'already_received' }>
/** One row of the table: a bag and the driver who carries it. */
type Carried = { bag: DriverBag; driverId: string; driverName: string }

const driversViaTrpc = async () => (await trpcClient.cashBags.Money.query()).drivers
const receiveViaTrpc = (input: CashBagReceiveRequest) => trpcClient.cashBags.Receive.mutate(input)

const failure = (error: unknown) => (error instanceof Error ? error.message : String(error))

/**
 * «Приём сумок» at the central cashbox (ADR-0036, ADR-0047; tickets 30, 57; user
 * story 107). A Cash bag has no seal and nothing on it to scan: the operator wrote
 * its Point and the date on it. So the page is one table — the bags the drivers
 * carry now, by Point, date and the operator's amount — and «Принять» on a row
 * opens the count in a dialog. The outcome stands above the table: a difference is
 * recorded, and whom it is against, the admin decides («Разницы сумок»).
 *
 * Enter moves on like Tab: the count → the comment → «Принять сумку», a second
 * Enter there confirms.
 *
 * `drivers` and `receive` are injected so the behaviour is tested without a backend.
 */
export function CashBagReceipt({
	drivers = driversViaTrpc,
	receive = receiveViaTrpc,
	onReceived,
}: {
	drivers?: () => Promise<DriverWithBags[]>
	receive?: (input: CashBagReceiveRequest) => Promise<CashBagReceiveAnswer>
	onReceived?: () => void
}) {
	const { t } = useTranslate()
	const [list, setList] = useState<DriverWithBags[] | null>(null)
	const [loadError, setLoadError] = useState<string | null>(null)
	const [chosen, setChosen] = useState<Carried | null>(null)
	const [result, setResult] = useState<Received | null>(null)

	const load = useCallback(() => {
		drivers().then(
			(next) => {
				setList(next)
				setLoadError(null)
			},
			(e: unknown) => setLoadError(failure(e)),
		)
	}, [drivers])
	useEffect(load, [load])

	const rows = useMemo<Carried[]>(
		() =>
			(list ?? []).flatMap((d) => d.bags.map((bag) => ({ bag, driverId: d.driverId, driverName: d.driverName }))),
		[list],
	)

	const columns = useMemo<ColumnDef<Carried>[]>(
		() => [
			{
				id: 'point',
				header: t('cashier.colPoint'),
				cell: ({ row }) => <span className='font-semibold'>{row.original.bag.pointCode}</span>,
			},
			{
				id: 'sealedAt',
				header: t('cashier.colPacked'),
				cell: ({ row }) => <span className='tabular-nums'>{formatDayTime(row.original.bag.sealedAt)}</span>,
			},
			{ id: 'operator', header: t('cashier.colOperator'), cell: ({ row }) => row.original.bag.sealedByName },
			{
				id: 'driver',
				header: t('cashier.colDriver'),
				cell: ({ row }) => (
					<span data-testid={`cash-receipt-driver-${row.original.driverId}`}>{row.original.driverName}</span>
				),
			},
			{
				id: 'counted',
				header: t('cashier.colOperatorCounted'),
				meta: MONEY_COLUMN,
				cell: ({ row }) => sum(row.original.bag.countedCash),
			},
			{
				id: 'actions',
				header: '',
				meta: { cellClassName: 'w-px text-right' },
				cell: ({ row }) => (
					<Button
						size='sm'
						variant='outline'
						data-testid={`cash-receipt-choose-${row.original.bag.cashBagId}`}
						onClick={() => {
							setResult(null)
							setChosen(row.original)
						}}
					>
						{t('cashier.choose')}
					</Button>
				),
			},
		],
		[t],
	)
	const { table } = useDataTable({
		data: rows,
		columns,
		rowCount: rows.length,
		...WHOLE,
		getRowId: (r) => r.bag.cashBagId,
	})

	return (
		<div className='flex flex-col gap-6'>
			{result && <ReceiveResult answer={result} />}
			{loadError && (
				<p role='alert' className='text-sm text-destructive' data-testid='cash-receipt-error'>
					{t('cashier.failed', { message: loadError })}
				</p>
			)}

			<section className='flex flex-col gap-2'>
				<SectionTitle
					title={t('cashier.bagsWithDrivers')}
					count={list === null ? undefined : rows.length}
					hint={t('cashier.receiveExplain')}
				/>
				<div data-testid='cash-receipt-drivers'>
					<DataTable
						table={table}
						withPagination={false}
						emptyText={
							list === null ? (
								t('money.loading')
							) : (
								<span data-testid='cash-receipt-none'>{t('cashier.receiveNone')}</span>
							)
						}
						row={{ attributes: (r) => ({ 'data-testid': `cash-receipt-bag-${r.bag.cashBagId}` }) }}
					/>
				</div>
			</section>

			<Dialog open={chosen !== null} onOpenChange={(open) => !open && setChosen(null)}>
				{chosen && (
					<ReceiveDialog
						chosen={chosen}
						receive={receive}
						onCancel={() => setChosen(null)}
						onReceived={(answer) => {
							setChosen(null)
							setResult(answer)
							load()
							onReceived?.()
						}}
					/>
				)}
			</Dialog>
		</div>
	)
}

/** «Принять сумку»: the bag as written on it, the amount counted, a comment if it differs. */
function ReceiveDialog({
	chosen,
	receive,
	onCancel,
	onReceived,
}: {
	chosen: Carried
	receive: (input: CashBagReceiveRequest) => Promise<CashBagReceiveAnswer>
	onCancel: () => void
	onReceived: (answer: Received) => void
}) {
	const { t } = useTranslate()
	const { bag } = chosen
	const [counted, setCounted] = useState('')
	const [comment, setComment] = useState('')
	const [sending, setSending] = useState(false)
	const [refusal, setRefusal] = useState<string | null>(null)
	const [error, setError] = useState<string | null>(null)
	const commentRef = useRef<HTMLInputElement>(null)
	const submitRef = useRef<HTMLButtonElement>(null)
	// A count that differs is explained: the cursor goes to the comment once the form is live again.
	const [askComment, setAskComment] = useState(0)
	useEffect(() => {
		if (askComment > 0 && !sending) commentRef.current?.focus()
	}, [askComment, sending])

	const receivedCash = counted.trim() === '' ? null : Number(counted)
	const difference = receivedCash !== null && Number.isFinite(receivedCash) ? receivedCash - bag.countedCash : 0
	const ready = receivedCash !== null && receivedCash >= 0 && !sending

	const submit = async () => {
		if (!ready || receivedCash === null) return
		setSending(true)
		setRefusal(null)
		setError(null)
		try {
			const res = await receive({
				cashBagId: bag.cashBagId,
				receivedCash,
				...(comment.trim() ? { comment: comment.trim() } : {}),
			})
			if (res.outcome === 'refused') {
				setRefusal(t(`cashBagRefusal.${res.reason}`))
				if (res.reason === 'cash_bag_comment_required') setAskComment((n) => n + 1)
				return
			}
			onReceived(res)
		} catch (e) {
			setError(failure(e))
		} finally {
			setSending(false)
		}
	}

	return (
		<DialogContent className='sm:max-w-md' data-testid='cash-receipt-form'>
			<DialogHeader>
				<DialogTitle>{t('cashier.submit')}</DialogTitle>
				<DialogDescription data-testid='cash-receipt-chosen'>
					{t('cashier.chosen', {
						point: bag.pointCode,
						date: formatDayTime(bag.sealedAt),
						operator: bag.sealedByName,
						driver: chosen.driverName,
					})}
				</DialogDescription>
			</DialogHeader>
			<div className='flex flex-col gap-4'>
				<Stats>
					<Stat label={t('cashier.colOperatorCounted')} value={sum(bag.countedCash)} />
				</Stats>
				<div className='flex flex-col gap-1.5'>
					<Label htmlFor='cash-receipt-counted'>{t('cashier.counted')}</Label>
					<Input
						id='cash-receipt-counted'
						data-testid='cash-receipt-counted'
						type='number'
						min={0}
						inputMode='numeric'
						autoComplete='off'
						autoFocus
						value={counted}
						onChange={(e) => setCounted(e.target.value)}
						onKeyDown={(e) => {
							if (e.key !== 'Enter') return
							e.preventDefault()
							commentRef.current?.focus()
						}}
					/>
					{difference !== 0 && (
						<p className='text-sm font-medium text-destructive' data-testid='cash-receipt-live-difference'>
							{t(difference < 0 ? 'cashier.shortage' : 'cashier.surplus', { amount: som(t, Math.abs(difference)) })}
						</p>
					)}
				</div>
				<div className='flex flex-col gap-1.5'>
					<Label htmlFor='cash-receipt-comment'>{t('cashier.comment')}</Label>
					<Input
						id='cash-receipt-comment'
						ref={commentRef}
						data-testid='cash-receipt-comment'
						autoComplete='off'
						value={comment}
						placeholder={t('cashier.commentPlaceholder')}
						onChange={(e) => setComment(e.target.value)}
						onKeyDown={(e) => {
							if (e.key !== 'Enter') return
							e.preventDefault()
							submitRef.current?.focus()
						}}
					/>
				</div>
				{refusal !== null && (
					<p role='alert' className='text-sm text-destructive' data-testid='cash-receipt-refused'>
						{refusal}
					</p>
				)}
				{error !== null && (
					<p role='alert' className='text-sm text-destructive' data-testid='cash-receipt-error'>
						{t('cashier.failed', { message: error })}
					</p>
				)}
			</div>
			<DialogFooter>
				<Button type='button' variant='outline' disabled={sending} data-testid='cash-receipt-other' onClick={onCancel}>
					{t('cashier.cancel')}
				</Button>
				<Button ref={submitRef} type='button' data-testid='cash-receipt-submit' disabled={!ready} onClick={submit}>
					{t('cashier.submit')}
				</Button>
			</DialogFooter>
		</DialogContent>
	)
}

/** What the count came to; a difference is recorded, and the admin decides whom it is against. */
function ReceiveResult({ answer }: { answer: Received }) {
	const { t } = useTranslate()
	const { bag } = answer
	const receipt = bag.receipt
	if (!receipt) return null
	const again = answer.outcome === 'already_received'
	const differs = receipt.difference !== 0
	return (
		<div data-testid='cash-receipt-result' data-difference={receipt.difference}>
			<ToneNote
				tone={again || differs ? 'attention' : 'ready'}
				title={t(again ? 'cashier.receivedBefore' : 'cashier.received', {
					point: bag.pointCode,
					amount: som(t, receipt.receivedCash),
				})}
				detail={
					<>
						{t('cashier.operatorCounted', { amount: som(t, bag.countedCash) })}{' '}
						<span className='font-semibold' data-testid='cash-receipt-difference'>
							{differs
								? t(receipt.difference < 0 ? 'cashier.shortageAdminDecides' : 'cashier.surplusAdminDecides', {
										amount: som(t, Math.abs(receipt.difference)),
									})
								: t('cashier.noDifference')}
						</span>
					</>
				}
			/>
		</div>
	)
}

/** The page «Приём сумок», with the central cashbox's other reads refreshed after a receipt. */
export function CashBagReceiptTab() {
	const queryClient = useQueryClient()
	return <CashBagReceipt onReceived={() => queryClient.invalidateQueries()} />
}
